import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Swords,
  Trophy,
  User as UserIcon,
  Users,
  Crown,
  Store,
  Sparkles,
  ChevronRight,
  type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '@fog-chess/core';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * HomeHubScreen - primary dashboard (Mandate C: dead-end protection).
 *
 * Active cards (never feature-flagged off in Phase 1):
 *   - Quick Match   -> /quick-match
 *   - Leaderboard   -> /leaderboard
 *   - Profile       -> /profile
 *
 * Future-phase cards gated by useFeatureFlag and rendered as visually
 * disabled with a "Coming Soon" overlay; clicks are blocked outright so
 * the user can never dead-end into an unimplemented screen:
 *   - Friend Play   -> rooms_enabled
 *   - Tournaments   -> tournaments_enabled
 *   - Store         -> tickets_enabled (alias "store")
 */

type Active =
  | { kind: 'active'; to: string; action: () => void }
  | { kind: 'disabled' };

interface HubCardConfig {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  accent: string;
  /** feature-flag alias routed through the core evaluator. null = always on. */
  flag: string | null;
  /** navigated to when the card is active. */
  to: string;
}

const CARDS: HubCardConfig[] = [
  {
    id: 'quick-match',
    title: 'Quick Match',
    description: 'Find a PvP opponent by FTPES stage or challenge the Fog Engine.',
    icon: Swords,
    accent: 'from-sky-500/20 to-sky-500/0',
    flag: 'quick-match',
    to: '/quick-match',
  },
  {
    id: 'friend-play',
    title: 'Friend Play',
    description: 'Spin up a private room with a code and invite a rival.',
    icon: Users,
    accent: 'from-emerald-500/20 to-emerald-500/0',
    flag: 'friend-rooms',
    to: '/friends',
  },
  {
    id: 'tournaments',
    title: 'Tournaments',
    description: 'Compete in single-elimination brackets for the crown.',
    icon: Crown,
    accent: 'from-amber-500/20 to-amber-500/0',
    flag: 'tournaments',
    to: '/tournaments',
  },
  {
    id: 'leaderboard',
    title: 'Leaderboard',
    description: 'Top 100 players, your global rank and FTPES stage.',
    icon: Trophy,
    accent: 'from-indigo-500/20 to-indigo-500/0',
    flag: 'leaderboard',
    to: '/leaderboard',
  },
  {
    id: 'profile',
    title: 'Profile & Stats',
    description: 'Your rating trajectory, wins/losses and match history.',
    icon: UserIcon,
    accent: 'from-rose-500/20 to-rose-500/0',
    flag: 'profile',
    to: '/profile',
  },
  {
    id: 'store',
    title: 'Store',
    description: 'Piece skins, ticket packs and cosmetic unlocks.',
    icon: Store,
    accent: 'from-fuchsia-500/20 to-fuchsia-500/0',
    flag: 'store',
    to: '/store',
  },
];

function resolveCardState(
  card: HubCardConfig,
  evaluate: (flag: string) => { enabled: boolean; isLoading: boolean },
): { active: Active; loading: boolean } {
  if (card.flag === null) {
    return { active: { kind: 'active', to: card.to, action: () => {} }, loading: false };
  }
  const status = evaluate(card.flag);
  return {
    loading: status.isLoading,
    active: status.enabled
      ? { kind: 'active', to: card.to, action: () => {} }
      : { kind: 'disabled' },
  };
}

function HubCard({
  card,
  index,
  active,
  loading,
  onSelect,
}: {
  card: HubCardConfig;
  index: number;
  active: Active;
  loading: boolean;
  onSelect: () => void;
}) {
  const Icon = card.icon;
  const disabled = active.kind === 'disabled';

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.06, ease: 'easeOut' }}
    >
      <Card
        role={disabled ? undefined : 'button'}
        tabIndex={disabled ? -1 : 0}
        onClick={disabled ? undefined : onSelect}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
        aria-disabled={disabled || undefined}
        className={cn(
          'group relative h-full overflow-hidden border-border/70 transition-colors',
          disabled
            ? 'cursor-not-allowed select-none opacity-60 grayscale'
            : 'cursor-pointer border-transparent hover:border-primary/40 hover:shadow-lg',
        )}
      >
        {/* Accent gradient wash */}
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity duration-500',
            card.accent,
            !disabled && 'group-hover:opacity-100',
          )}
        />
        <CardContent className="relative flex h-full flex-col gap-3 p-6">
          <div className="flex items-start justify-between">
            <span
              className={cn(
                'flex h-12 w-12 items-center justify-center rounded-xl shadow-sm transition-transform',
                disabled
                  ? 'bg-muted text-muted-foreground'
                  : 'bg-primary/10 text-primary group-hover:scale-105',
              )}
            >
              <Icon className="h-6 w-6" />
            </span>
            {loading ? (
              <Skeleton className="h-6 w-20 rounded-full" />
            ) : disabled ? (
              <Badge variant="fog" className="gap-1">
                <Sparkles className="h-3 w-3" />
                Coming Soon
              </Badge>
            ) : null}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold tracking-tight">
              {card.title}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {card.description}
            </p>
          </div>
          {!disabled && (
            <div className="flex items-center gap-1 text-sm font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100">
              Open
              <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function HomeHubScreen() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  // Each flag resolves independently so a partial outage only dims the card
  // it governs. We batch the three future-phase cards behind three hook
  // instances (rules-of-hooks: count + order are stable across renders).
  const friendPlay = useFeatureFlag('friend-rooms');
  const tournaments = useFeatureFlag('tournaments');
  const store = useFeatureFlag('store');
  // Active cards always include their own flag check too (so backend can
  // operationally disable PvP / replays without a deploy). The evaluator
  // degrades gracefully to "enabled" via defaults if the fetch is loading.
  const quickMatch = useFeatureFlag('quick-match');
  const leaderboard = useFeatureFlag('leaderboard');
  const profile = useFeatureFlag('profile');

  const evaluate = (flag: string) => {
    switch (flag) {
      case 'quick-match':
        return { enabled: quickMatch.enabled, isLoading: quickMatch.isLoading };
      case 'leaderboard':
        return {
          enabled: leaderboard.enabled,
          isLoading: leaderboard.isLoading,
        };
      case 'profile':
        return { enabled: profile.enabled, isLoading: profile.isLoading };
      case 'friend-rooms':
        return {
          enabled: friendPlay.enabled,
          isLoading: friendPlay.isLoading,
        };
      case 'tournaments':
        return {
          enabled: tournaments.enabled,
          isLoading: tournaments.isLoading,
        };
      case 'store':
        return { enabled: store.enabled, isLoading: store.isLoading };
      default:
        return { enabled: false, isLoading: false };
    }
  };

  const handleSelect = (to: string) => navigate(to);

  return (
    <div className="space-y-8 py-4">
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col gap-1"
      >
        <h1 className="text-3xl font-bold tracking-tight">
          Welcome back, {user?.username ?? 'Commander'}
        </h1>
        <p className="text-muted-foreground">
          Choose your next move in the fog.
        </p>
      </motion.section>

      <section>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {CARDS.map((card, index) => {
            const { active, loading } = resolveCardState(card, evaluate);
            const onCard =
              active.kind === 'active'
                ? () => handleSelect(active.to)
                : undefined;
            return (
              <HubCard
                key={card.id}
                card={card}
                index={index}
                active={active}
                loading={loading}
                onSelect={onCard ?? (() => {})}
              />
            );
          })}
        </div>
      </section>

      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="rounded-xl border border-border/60 bg-card/60 p-5"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-fog/15 text-fog-dark dark:text-fog-light">
              <Sparkles className="h-5 w-5" />
            </span>
            <div>
              <p className="font-medium leading-tight">What is FTPES?</p>
              <p className="text-sm text-muted-foreground">
                Five-tier Fog Tiered Progression & Elo System — your rating
                unlocks harder opponents and PvP matchmaking.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/profile')}
            className="shrink-0"
          >
            View my tier
          </Button>
        </div>
      </motion.section>
    </div>
  );
}
